<script setup lang="ts">
import tradutor from "@/tradutors/rightnavbar";

import { useSocialNetworks, socialNetworksFor } from "@/composables/useSocialNetworks";

import FlagUnitedStates from "assets/img/Flag_of_the_United_States.webp";
import FlagBrasil from "assets/img/Flag_of_Brazil.webp";

interface MenuCardItem {
  name: string;
  route: string;
  active: string[];
  query?: Record<string, any>;
  activeQuery?: Record<string, string | number>;
  gridPos?: string;
}

const emits = defineEmits(["showMenuLateral", "showModalSubscribePlans"]);

const storeUser = userStore();
const storeJwt = jwtStore();
const router = useRouter();

const route = useRoute();
const reactiveLocale = computed(() => route.params.language);
const reactiveisLoggedIn = computed(() => storeUser.isLoggedIn);

const setIdLang = ref(reactiveLocale.value === "en" ? 1 : 0);

const translate = computed(() => {
  if (reactiveLocale.value === "pt-br") {
    return tradutor.portugues;
  } else {
    return tradutor.ingles;
  }
});

// Mesma chave de useAsyncData do Header e do Footer: uma requisição serve os três.
const socialNetworks = await useSocialNetworks(String(reactiveLocale.value));
const icones = computed(() => socialNetworksFor(socialNetworks.value, "menu"));

watch(reactiveLocale, (val) => {
  setIdLang.value = val === "en" ? 1 : 0;
});

function isActive(...routeNames: string[]): boolean {
  const current = String(route.name ?? "");
  return routeNames.some((n) => current === n || current.includes(n));
}

function isCardActive(item: MenuCardItem): boolean {
  if (item.active.length > 0 && !isActive(...item.active)) return false;
  if (item.active.length === 0 && !item.activeQuery) return false;
  if (item.activeQuery) {
    for (const [key, expected] of Object.entries(item.activeQuery)) {
      const raw = route.query[key];
      const actual = Array.isArray(raw) ? raw[0] ?? "" : raw ?? "";
      const expectedStr = String(expected);
      // tab=0 é o default: aceitar query ausente como equivalente
      if (expectedStr === "0" && actual === "") continue;
      if (String(actual) !== expectedStr) return false;
    }
  }
  return true;
}

const cards = computed<MenuCardItem[]>(() => {
  const t = translate.value;
  const first: MenuCardItem = reactiveisLoggedIn.value
    ? { name: t.menu.myAccountLogado, route: "language-minhaconta", active: ["language-minhaconta"], query: { tab: 0 }, activeQuery: { tab: 0 } }
    : { name: t.menu.myAccount,        route: "language-login",     active: ["language-login"] };
  return [
    first,
    { name: t.menu.bellas,     route: "language-modelos",    active: ["language-modelos"] },
    { name: t.menu.blog,       route: "language-blog",       active: ["language-blog"] },
    { name: t.menu.videos,     route: "language-videos",     active: ["language-videos"] },
    { name: t.menu.makingof,   route: "language-making-of",  active: ["language-making-of"] },
    { name: t.menu.lives,      route: "language-lives",      active: ["language-lives"] },
    { name: t.menu.colums,     route: "language-colunas",    active: ["language-colunas"] },
    { name: t.menu.ecards,     route: "language-ecards",     active: ["language-ecards"] },
    { name: t.menu.bellacloud, route: "language-bellacloud", active: ["language-bellacloud"] },
  ];
});

const subscribeCta = computed(() => {
  const t = translate.value.menu;
  if (!reactiveisLoggedIn.value) return t.subscribeButton;
  return storeUser.isActiveUser ? t.upgradeButton : t.renewButton;
});

function closeMenu() {
  emits("showMenuLateral", false);
}
function redirect(name: string) {
  router.push({ name, params: { language: reactiveLocale.value } });
}

function goTo(item: { route: string; query?: Record<string, any> }) {
  closeMenu();
  router.push({
    name: item.route,
    params: { language: reactiveLocale.value },
    ...(item.query ? { query: item.query } : {}),
  });
}

async function logout() {
  closeMenu();
  storeJwt.jwtLogout();
  redirect("language");
}
</script>
<template>
  <nav class="new_menu__nav">
    <div class="d-flex div-header">
      <div class="new_menu__header">
        <div class="menu-hifens" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <span class="menu-label">{{ translate.menu.menuLabel }}</span>
      </div>
      <div
        class="d-flex flex-fill align-items-center justify-content-center bg-red"
      >
        <a
          @click.prevent="closeMenu"
          href="#"
          class="w-50 h-50 d-flex justify-content-center close-x"
          >x</a
        >
      </div>
    </div>

    <div class="new_menu__body">
      <div class="new_menu__grid">
        <button
          v-for="(item, i) in cards"
          :key="`c-${i}`"
          type="button"
          class="menu-card"
          :class="{ 'menu-card--active': isCardActive(item) }"
          @click="goTo(item)"
        >
          {{ item.name }}
        </button>
      </div>

      <button
        type="button"
        class="menu-subscribe-btn"
        @click="goTo({ route: 'language-assine' })"
      >
        {{ subscribeCta }}
      </button>

      <!-- Links de utilidade (rodapé textual) -->
      <ClientOnly>
        <ul class="new_menu__util">
          <li
            :class="[
              'util-link', 'd-flex', 'justify-content-center',
              { 'util-link--active': isActive('language-sobre') },
              reactiveisLoggedIn ? 'col-4' : 'col-6',
            ]"
            @click="redirect('language-sobre')"
          >
            {{ translate.menu.sobre }}<i class="icon-arrow-ios-right"></i>
          </li>
          <li
            :class="[
              'util-link', 'd-flex', 'justify-content-center',
              { 'util-link--active': isActive('language-contato') },
              reactiveisLoggedIn ? 'col-5' : 'col-6',
            ]"
            @click="redirect('language-contato')"
          >
            {{ translate.menu.contactUs }}<i class="icon-arrow-ios-right"></i>
          </li>
          <li
            v-if="reactiveisLoggedIn"
            class="util-link d-flex justify-content-center col-3"
            @click.prevent="logout"
          >
            {{ translate.menu.logout }}<i class="icon-arrow-ios-right"></i>
          </li>
        </ul>
      </ClientOnly>

      <!-- Social Links -->
      <ul
        v-if="icones.length"
        class="social-box social-box-right justify-content-center"
      >
        <li
          class="d-flex justify-content-center"
          :class="`social-icon--${icon.slug}`"
          v-for="icon in icones"
          :key="icon.slug"
        >
          <a :href="icon.url" target="_blank" rel="noopener noreferrer"
            ><img
              :src="icon.icon"
              :alt="icon.name"
              width="25"
              height="25"
              class="icon-header-right"
          /></a>
        </li>
      </ul>
    </div>
  </nav>
</template>

<style scoped lang="scss">
.bm-burger-button {
  position: fixed;
  top: 26px;
  right: 18px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 16px;
  z-index: 999;

  @media (min-width: 769px) {
    right: 43px !important;
  }
}

.bm-burger-bars {
  background-color: #373a47;
  border-radius: 25%;
}

.line-style {
  position: absolute;
  height: 3px;
  left: 0;
  right: 0;
}

.cross-style {
  position: absolute;
  top: 12px;
  right: 2px;
  cursor: pointer;
}

.bm-cross {
  background: #bdc3c7;
}

.bm-cross-button {
  height: 30px;
  width: 30px;
  right: 4px;
}

.bm-menu {
  height: 100vh;
  /* 100% Full-height */
  width: 0;
  /* 0 width - change this with JavaScript */
  position: fixed;
  /* Stay in place */
  z-index: 1000;
  /* Stay on top */
  top: 0;
  left: 0;
  background-color: rgb(63, 63, 65);
  /* Black*/
  overflow-x: hidden;
  /* Disable horizontal scroll */
  padding-top: 0;
  /* Place content 60px from the top */
  transition: 0.5s;
  /*0.5 second transition effect to slide in the sidenav*/
}

.bm-overlay {
  background: rgba(0, 0, 0, 0.6);
}

.bm-item-list {
  color: #b8b7ad;
  margin-left: 0;
  font-size: 20px;
  height: 100%;
}

.bm-item-list > * {
  display: block;
  text-decoration: none;
  padding: 0;
}

.bm-item-list > * > span {
  margin-left: 10px;
  font-weight: 700;
  color: white;
}

.new_menu__header {
  color: #fff;
  height: 65px;
  display: flex;
  align-items: center;
  padding-left: 20px;
}

.new_menu__nav {
  height: unset;
  box-shadow: rgba(0, 0, 0, 0.66) 0px 22px 70px 100000px;
  border-radius: 20px;
}

.language-nav-mobile {
  width: 60px;
  height: 24px;
  margin-right: 30px;
}

.language-nav-mobile a img {
  border-radius: 50%;
  width: 22px;
  height: 22px;
  object-fit: cover;
  top: -2px;
}
.language-nav-mobile a {
  font-family: "Exo-2";
  vertical-align: unset;
  top: 0px;
}

.language-nav-mobile .dropdown-toggle:after {
  top: -1px;
  color: rgba(0, 0, 0, 0.5);
}

.language-nav-mobile .dropdown-menu.show {
  background: #fff;
}

.new_menu__body {
  padding: 0px 0px;
}

.social-box {
  height: 60px;
  top: -8px;
  padding: 0 20px;
  justify-content: space-between !important;
}

.social-box-right li {
  width: auto;
  margin-right: unset;
}

.bg-red {
  background-color: #b20000;
}

.close-x {
  top: -16px;
  font-size: 35px;
  font-weight: 900;
  color: #fff;
}

.icon-header-right {
  width: 33px;
  height: 33px;
}

/* ===== Grid de cards (novo layout) ===== */
.new_menu__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 16px 20px;
  justify-content: center;
}

.menu-card {
  background: #DDDDDD87;
  border: none;
  border-radius: 10px;
  font-family: "Roboto-Regular", Roboto, Arial, sans-serif;
  font-weight: 400;
  font-size: 15px;
  color: #b20000;
  min-height: 64px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.15;
  transition: background-color 0.15s ease;
}

.menu-card:hover {
  background: #CBCBCB;
}
.menu-card.menu-card--active {
  background: #CBCBCB;
  font-family: "Roboto-Bold", Roboto, Arial, sans-serif;
  font-weight: 700;
}

/* ===== CTA de assinatura ===== */
.menu-subscribe-btn {
  display: block;
  width: 40%;
  margin: 20px auto 20px;
  height: 35px;
  background: #b20000;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-family: "Roboto-Bold";
  font-size: 18px;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.menu-subscribe-btn:hover {
  background: #9a0000;
}
.menu-subscribe-btn:active {
  transform: translateY(1px);
}

/* ===== Cabeçalho hamburger + MENU ===== */
.menu-hifens {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
  margin-right: 5px;
}
.menu-hifens span {
  display: block;
  width: 20px;
  height: 3px;
  background: #fff;
  border-radius: 3px;
}
.new_menu__header .menu-label {
  font-family: "Bebas-Neue";
  font-size: 22px;
  letter-spacing: 1px;
  color: #ffffff;
  top: 2.5px;
}

.new_menu__util {
  list-style: none;
  padding: 8px 20px 12px;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0px;
  justify-content: end;
}

.util-link {
  font-family: "Roboto-Regular", Roboto, Arial, sans-serif;
  font-weight: 400;
  color: #5B5858;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  i {
    color: #5B5858;
    margin-left: -4px;
  }

  &--active {
    font-family: "Roboto-Bold", Roboto, Arial, sans-serif;
    font-weight: 700;
  }
}

i[class*=icon-]:before {
  color: #000000;
  opacity: 0.5;
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
  .div-header {
    height: 65px;
  }

  .new_menu__header {
    height: 65px;
  }

  .flex-fill {
    width: 66px;
  }

  .close-x {
    top: -20px;
  }

  .icon-header-right {
    width: 25px;
    height: 25px;
  }

  .social-box {
    padding: 0 16px;
    height: 50px;
  }

  .social-box-right li {
    width: auto;
    margin-right: unset;
  }

  .new_menu__grid {
    gap: 8px;
    padding: 12px 16px;
  }

  .menu-card {
    font-size: 13px;
    min-height: 56px;
  }

  .menu-subscribe-btn {
    font-size: 16px;
  }

  .menu-hifens span {
    width: 16px;
  }

  .new_menu__util {
    gap: 0px;
  }

  .util-link {
    font-size: 12px;
  }

  :deep(.util-link) {
    line-height: 0px;
  }  

}
</style>
